import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";
import {
  Building2, MapPin, Wrench, ShieldCheck, FileSignature,
  Upload, X, Send, FileText, Image as ImageIcon, AlertCircle,
} from "lucide-react";

const SERVICE_CATEGORIES = [
  "Engine", "Transmission", "Brakes", "Electrical", "Suspension",
  "Body", "Tires", "HVAC", "Exhaust", "Diagnostics", "EV",
];
const BUSINESS_TYPES = ["LLC", "Sole Proprietor", "S-Corp", "C-Corp", "Partnership", "Other"];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const WARRANTY_OPTIONS = ["Standard 12 mo / 12k mi", "Custom", "None"];

const blank = {
  legal_business_name: "", dba_name: "", business_type: "", year_founded: "",
  owner_full_name: "", owner_phone: "", tax_id: "",
  is_mobile: false,
  address: { street: "", city: "", state: "", zip: "" },
  service_radius_miles: "",
  shop_photos: [],
  service_categories: [],
  years_experience: "",
  warranty_offered: "",
  license_number: "", license_state: "", license_document: "",
  insurance_carrier: "", insurance_policy_number: "", insurance_coi_document: "",
  agreements: { tos: false, privacy: false, anonymity: false, pricing_transparency: false, dispute_resolution: false },
};

export default function ShopApplication() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState(blank);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/shops/application");
        if (res.data) setForm({ ...blank, ...res.data, address: { ...blank.address, ...(res.data.address || {}) }, agreements: { ...blank.agreements, ...(res.data.agreements || {}) } });
      } catch {}
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));
  const setAgree = (k, v) => setForm((f) => ({ ...f, agreements: { ...f.agreements, [k]: v } }));
  const toggleCategory = (c) => set("service_categories", form.service_categories.includes(c) ? form.service_categories.filter((x) => x !== c) : [...form.service_categories, c]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        year_founded: parseInt(form.year_founded) || 0,
        years_experience: parseInt(form.years_experience) || 0,
        service_radius_miles: parseInt(form.service_radius_miles) || 0,
      };
      await api.post("/shops/application", payload);
      toast.success("Application submitted — admin will review shortly");
      await checkAuth();
      navigate("/pending");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.errors) {
        toast.error(detail.errors[0]);
        console.warn("Application errors:", detail.errors);
      } else {
        toast.error(detail || "Failed to submit");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#09090B] safe-top safe-bottom">
      <Navbar />
      <main className="max-w-[1100px] mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid="shop-application-page">
        <div className="label-eyebrow mb-3">SHOP APPLICATION</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">Tell us about your shop.</h1>
        <p className="text-zinc-400 mb-10 max-w-2xl">15 fields. Should take ~15 minutes. All documents are private — only admins see them.</p>

        <div className="space-y-8">
          {/* 1. Identity */}
          <Section icon={Building2} title="1. Business identity" hint="Matches your license and insurance documents.">
            <Grid cols={2}>
              <Field label="LEGAL BUSINESS NAME *">
                <input className="input-base" value={form.legal_business_name} onChange={(e) => set("legal_business_name", e.target.value)} data-testid="app-legal-name" />
              </Field>
              <Field label="DBA / TRADE NAME">
                <input className="input-base" value={form.dba_name} onChange={(e) => set("dba_name", e.target.value)} data-testid="app-dba-name" />
              </Field>
              <Field label="BUSINESS TYPE *">
                <select className="input-base" value={form.business_type} onChange={(e) => set("business_type", e.target.value)} data-testid="app-business-type">
                  <option value="">Select…</option>
                  {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="YEAR FOUNDED *">
                <input className="input-base" type="number" min="1900" value={form.year_founded} onChange={(e) => set("year_founded", e.target.value)} data-testid="app-year-founded" />
              </Field>
              <Field label="OWNER FULL NAME *">
                <input className="input-base" value={form.owner_full_name} onChange={(e) => set("owner_full_name", e.target.value)} data-testid="app-owner-name" />
              </Field>
              <Field label="OWNER PHONE *">
                <input className="input-base" type="tel" value={form.owner_phone} onChange={(e) => set("owner_phone", e.target.value)} placeholder="(415) 555-0142" data-testid="app-owner-phone" />
              </Field>
              <Field label="TAX ID / EIN *" hint="Format: 12-3456789 — kept encrypted, admin-only">
                <input className="input-base" value={form.tax_id} onChange={(e) => set("tax_id", e.target.value)} data-testid="app-tax-id" />
              </Field>
            </Grid>
          </Section>

          {/* 2. Location */}
          <Section icon={MapPin} title="2. Location & service area" hint="If you're a mobile shop, toggle below and upload photos of your rig instead of a storefront.">
            <div className="mb-5">
              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_mobile} onChange={(e) => set("is_mobile", e.target.checked)} data-testid="app-is-mobile" className="w-4 h-4 accent-[#FF3B30]" />
                <span>I run a mobile shop (no fixed storefront)</span>
              </label>
            </div>
            {!form.is_mobile && (
              <Grid cols={2}>
                <Field label="STREET ADDRESS *" full>
                  <input className="input-base" value={form.address.street} onChange={(e) => setAddr("street", e.target.value)} data-testid="app-addr-street" />
                </Field>
                <Field label="CITY *">
                  <input className="input-base" value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} data-testid="app-addr-city" />
                </Field>
                <Field label="STATE *">
                  <select className="input-base" value={form.address.state} onChange={(e) => setAddr("state", e.target.value)} data-testid="app-addr-state">
                    <option value="">—</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="ZIP *">
                  <input className="input-base" value={form.address.zip} onChange={(e) => setAddr("zip", e.target.value)} data-testid="app-addr-zip" />
                </Field>
              </Grid>
            )}
            <Grid cols={2} className="mt-5">
              <Field label="SERVICE RADIUS (MILES) *" hint="How far will you travel for a job?">
                <input className="input-base" type="number" min="1" max="500" value={form.service_radius_miles} onChange={(e) => set("service_radius_miles", e.target.value)} data-testid="app-radius" />
              </Field>
            </Grid>
            <div className="mt-6">
              <FileMultiUploader
                label={`${form.is_mobile ? "MOBILE RIG" : "SHOP"} PHOTOS * (MIN 3)`}
                paths={form.shop_photos}
                onChange={(p) => set("shop_photos", p)}
                accept="image/*"
                testid="app-photos"
              />
            </div>
          </Section>

          {/* 3. Capabilities */}
          <Section icon={Wrench} title="3. Capabilities" hint="Helps us match the right jobs to you.">
            <Field label="SERVICE CATEGORIES * (PICK AT LEAST ONE)" full>
              <div className="flex flex-wrap gap-2" data-testid="app-categories">
                {SERVICE_CATEGORIES.map((c) => {
                  const active = form.service_categories.includes(c);
                  return (
                    <button key={c} type="button" onClick={() => toggleCategory(c)}
                      className={`text-xs uppercase tracking-widest px-3 py-2 transition-colors ${active ? "bg-[#FF3B30] text-white" : "border border-white/15 text-zinc-400 hover:text-white hover:border-white/30"}`}
                      data-testid={`app-cat-${c.toLowerCase()}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Grid cols={2} className="mt-5">
              <Field label="YEARS OF BUSINESS EXPERIENCE *">
                <input className="input-base" type="number" min="0" max="100" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} data-testid="app-years-exp" />
              </Field>
              <Field label="WARRANTY OFFERED *">
                <select className="input-base" value={form.warranty_offered} onChange={(e) => set("warranty_offered", e.target.value)} data-testid="app-warranty">
                  <option value="">Select…</option>
                  {WARRANTY_OPTIONS.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </Field>
            </Grid>
          </Section>

          {/* 4. License & Insurance */}
          <Section icon={ShieldCheck} title="4. License & insurance" hint="We verify these before approving. Documents are admin-only.">
            <Grid cols={2}>
              <Field label="STATE LICENSE NUMBER *">
                <input className="input-base" value={form.license_number} onChange={(e) => set("license_number", e.target.value)} data-testid="app-license-num" />
              </Field>
              <Field label="LICENSE ISSUING STATE *">
                <select className="input-base" value={form.license_state} onChange={(e) => set("license_state", e.target.value)} data-testid="app-license-state">
                  <option value="">—</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </Grid>
            <div className="mt-5">
              <FileSingleUploader
                label="LICENSE DOCUMENT * (PDF OR IMAGE)"
                path={form.license_document}
                onChange={(p) => set("license_document", p)}
                accept="image/*,application/pdf"
                testid="app-license-doc"
              />
            </div>
            <Grid cols={2} className="mt-6">
              <Field label="INSURANCE CARRIER *">
                <input className="input-base" value={form.insurance_carrier} onChange={(e) => set("insurance_carrier", e.target.value)} data-testid="app-ins-carrier" placeholder="e.g. The Hartford" />
              </Field>
              <Field label="POLICY NUMBER *">
                <input className="input-base" value={form.insurance_policy_number} onChange={(e) => set("insurance_policy_number", e.target.value)} data-testid="app-ins-policy" />
              </Field>
            </Grid>
            <div className="mt-5">
              <FileSingleUploader
                label="CERTIFICATE OF INSURANCE (COI) * (PDF OR IMAGE)"
                path={form.insurance_coi_document}
                onChange={(p) => set("insurance_coi_document", p)}
                accept="image/*,application/pdf"
                testid="app-coi-doc"
              />
            </div>
          </Section>

          {/* 5. Agreements */}
          <Section icon={FileSignature} title="5. Agreements" hint="All five are required. These protect car owners and the platform.">
            <div className="space-y-3">
              <Agreement k="tos" checked={form.agreements.tos} onChange={setAgree} label="I agree to the MechaniCar Terms of Service." />
              <Agreement k="privacy" checked={form.agreements.privacy} onChange={setAgree} label="I acknowledge the Privacy Policy." />
              <Agreement k="anonymity" checked={form.agreements.anonymity} onChange={setAgree} label="Anonymity policy — I will not attempt to identify, photograph, or contact car owners outside the app." important />
              <Agreement k="pricing_transparency" checked={form.agreements.pricing_transparency} onChange={setAgree} label="Pricing transparency — I will honor my written quote unless the customer pre-approves any change in scope." />
              <Agreement k="dispute_resolution" checked={form.agreements.dispute_resolution} onChange={setAgree} label="I agree to abide by MechaniCar's dispute-resolution process if a customer raises a complaint." />
            </div>
          </Section>

          {/* Submit */}
          <div className="border border-white/10 bg-[#121214] p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-sm text-zinc-400">
              <AlertCircle className="w-4 h-4 text-[#FF9500]" />
              You can edit and resubmit before an admin reviews your application.
            </div>
            <button onClick={submit} disabled={submitting} className="btn-primary flex items-center gap-2" data-testid="app-submit-btn">
              <Send className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit application"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, hint, children }) {
  return (
    <section className="border border-white/10 bg-[#121214]">
      <div className="px-6 py-5 border-b border-white/10 flex items-start gap-3">
        <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-white" strokeWidth={2} />
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold tracking-tight">{title}</h2>
          {hint && <p className="text-xs text-zinc-500 mt-0.5">{hint}</p>}
        </div>
      </div>
      <div className="px-6 py-6">{children}</div>
    </section>
  );
}

function Grid({ cols = 2, className = "", children }) {
  return <div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-4 ${className}`}>{children}</div>;
}

function Field({ label, hint, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="label-eyebrow block mb-2">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-zinc-600 mt-1.5">{hint}</p>}
    </div>
  );
}

function Agreement({ k, checked, onChange, label, important }) {
  return (
    <label className={`flex items-start gap-3 text-sm cursor-pointer p-3 border ${checked ? "border-[#34C759]/40 bg-[#34C759]/5" : "border-white/10"} ${important ? "border-[#FF3B30]/40 bg-[#FF3B30]/5" : ""}`} data-testid={`app-agree-${k}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(k, e.target.checked)} className="mt-1 w-4 h-4 accent-[#FF3B30] shrink-0" />
      <span className={`${important ? "text-white" : "text-zinc-300"}`}>{label}</span>
    </label>
  );
}

function FileSingleUploader({ label, path, onChange, accept, testid }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.path);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <div className="border border-dashed border-white/20 bg-[#09090B] p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {path ? (
            <>
              <FileText className="w-5 h-5 text-[#34C759] shrink-0" />
              <span className="text-xs font-mono truncate text-zinc-400">{path.split("/").pop()}</span>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 text-zinc-600 shrink-0" />
              <span className="text-sm text-zinc-500">No file uploaded yet.</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={() => ref.current?.click()} className="btn-secondary text-xs py-2 px-4" data-testid={`${testid}-pick`}>
            {uploading ? "…" : path ? "Replace" : "Upload"}
          </button>
          {path && (
            <button type="button" onClick={() => onChange("")} className="text-zinc-500 hover:text-[#FF3B30]" data-testid={`${testid}-clear`}>
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={handle} data-testid={`${testid}-input`} />
    </div>
  );
}

function FileMultiUploader({ label, paths, onChange, accept, testid }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const next = [...paths];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push(res.data.path);
      } catch { toast.error(`Upload failed: ${f.name}`); }
    }
    onChange(next);
    setUploading(false);
  };

  const remove = (p) => onChange(paths.filter((x) => x !== p));

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="label-eyebrow">{label}</label>
        <button type="button" onClick={() => ref.current?.click()} className="btn-secondary text-xs py-2 flex items-center gap-1.5" data-testid={`${testid}-pick`}>
          <Upload className="w-3.5 h-3.5" /> {uploading ? "…" : "Add photos"}
        </button>
        <input ref={ref} type="file" accept={accept} multiple className="hidden" onChange={handle} data-testid={`${testid}-input`} />
      </div>
      {paths.length === 0 ? (
        <div className="border border-dashed border-white/20 p-6 text-center text-sm text-zinc-500">
          Upload at least 3 photos showing your shop / mobile rig.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {paths.map((p) => (
            <div key={p} className="relative aspect-square border border-white/10 bg-[#09090B] flex items-center justify-center" data-testid={`${testid}-item-${p}`}>
              <ImageIcon className="w-6 h-6 text-zinc-700" />
              <button onClick={() => remove(p)} className="absolute top-1 right-1 bg-[#09090B]/80 text-zinc-400 hover:text-[#FF3B30] p-1">
                <X className="w-3.5 h-3.5" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-[#09090B]/80 px-2 py-1 text-[10px] font-mono truncate text-zinc-500">{p.split("/").pop()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
